import { Backdrop, CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import axios from "axios";

import { BackdropSection } from "../styles/style";


// Excluded URL patterns
const excludedUrls = [
  '/api/users/',
  '/api/useraccess/userPermission/',
  '/api/roles',
  '/api/useraccess/',
];

const isExcludedUrl = (url: string) => excludedUrls.some((pattern) => url.includes(pattern));
const isUserModifiedURL = (url: string) => url.includes('isUserModified');
const shouldHandleUrl = (url: string) => !isExcludedUrl(url);

const setLoadingIfNeeded = (
  pendingCountsRef: { current: Map<string, number> },
  userModifiedCountRef: { current: number },
  setLoading: (v: boolean) => void
) => {
  if (pendingCountsRef.current.size === 0 && userModifiedCountRef.current === 0) {
    setLoading(false);
  }
};

const incrementPending = (
  pendingCountsRef: { current: Map<string, number> },
  url: string
) => {
  const map = pendingCountsRef.current;
  const count = map.get(url) || 0;
  map.set(url, count + 1);
};

const decrementPending = (
  pendingCountsRef: { current: Map<string, number> },
  url: string
) => {
  const map = pendingCountsRef.current;
  const count = map.get(url) || 0;
  if (count <= 1) {
    map.delete(url);
  } else {
    map.set(url, count - 1);
  }
};

const handleRequestStart = (
  url: string,
  userModifiedCountRef: { current: number },
  pendingCountsRef: { current: Map<string, number> },
  setLoading: (v: boolean) => void
) => {
  if (isUserModifiedURL(url)) {
    userModifiedCountRef.current += 1;
    setLoading(true);
    return;
  }
  incrementPending(pendingCountsRef, url);
  setLoading(true);
};

const handleRequestFinish = (
  url: string,
  userModifiedCountRef: { current: number },
  pendingCountsRef: { current: Map<string, number> },
  setLoading: (v: boolean) => void
) => {
  if (isUserModifiedURL(url)) {
    userModifiedCountRef.current = Math.max(0, userModifiedCountRef.current - 1);
    setLoadingIfNeeded(pendingCountsRef, userModifiedCountRef, setLoading);
    return;
  }
  decrementPending(pendingCountsRef, url);
  setLoadingIfNeeded(pendingCountsRef, userModifiedCountRef, setLoading);
};

function CentralLoader(){
  const [loading, setLoading] = useState(true);
  const pendingCountsRef = useRef<Map<string, number>>(new Map());
  const userModifiedCountRef = useRef<number>(0);

  useEffect(() => {
    // Add a request interceptor
    const reqInterceptor = axios.interceptors.request.use(
      (config) => {
        const url = config.url || '';
        if (shouldHandleUrl(url)) {
          handleRequestStart(url, userModifiedCountRef, pendingCountsRef, setLoading);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add a response interceptor
    const resInterceptor = axios.interceptors.response.use(
      (response) => {
        const url = response.config.url || '';
        if (shouldHandleUrl(url)) {
          handleRequestFinish(url, userModifiedCountRef, pendingCountsRef, setLoading);
        }
        return response;
      },
      (error) => {
        const url = error.config?.url || '';
        if (shouldHandleUrl(url)) {
          handleRequestFinish(url, userModifiedCountRef, pendingCountsRef, setLoading);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, []);

  return (
    <Backdrop sx={BackdropSection} open={loading}>
      <CircularProgress />
    </Backdrop>
  );
}

export default CentralLoader;
